<template>
  <div
    :class="{ 'configurator-texture-canvas--headless': headless }"
    class="configurator-texture-canvas"
  >
    <v-stage ref="stage" :config="stageConfig">
      <v-layer>
        <v-rect v-if="hasBackground" :config="backgroundRectConfig" />

        <v-group :config="printingAreaConfig">
          <v-rect v-if="debugPrintingArea" :config="printingAreaDebugConfig" />

          <ConfiguratorTextureCanvasLayerFactory
            v-for="layer in imageLayers"
            :key="layer._customization.id"
            ref="layers"
            :layer="layer"
            :debug="debugPrintingArea"
            :color="color"
          />

          <v-image v-if="textureImg" :config="textureImgConfig" />

          <ConfiguratorTextureCanvasLayerFactory
            v-for="layer in textLayers"
            :key="layer._customization.id"
            ref="layers"
            :layer="layer"
            :debug="debugPrintingArea"
            :color="color"
          />
        </v-group>

        <v-image v-if="hasGraduation" :config="graduationImgConfig" />
      </v-layer>
    </v-stage>
  </div>
</template>

<script>
import CanvasExport from '../../mixins/CanvasExport';
import PromiseHelper from '../../utils/PromiseHelper';
import { getPerimeter, mmToPx, Resolution, scale } from '../../utils/MathUtils';
import {
  canvasToBlob,
  checkImageSizeNotZero,
  cropImage,
  loadImageAndCache,
} from '../../utils/ImageUtils';
import ConfiguratorTextureCanvasLayerFactory from './ConfiguratorTextureCanvasLayerFactory';
import ConfiguratorTextureCanvas from '../../mixins/ConfiguratorTextureCanvas';
import { mapState as mapMiscState } from '../../store/modules/configurator/misc';
import { transformProps } from '../../utils/ObjectUtils';
import { ZoneType } from '../../api/static/zones';
import cloneDeep from 'lodash/cloneDeep';
import {
  ConfiguratorGetters,
  mapGetters as mapConfiguratorGetters,
} from '../../store/modules/configurator.js';

const PromiseKey = Object.freeze({
  DOM_READY: 0,
  TEXTURE_IMAGE: 1,
  LAYERS_READY: 2,
  LAYERS_PROCESSING: 3,
});

export default {
  name: 'ConfiguratorTextureCanvas',
  components: { ConfiguratorTextureCanvasLayerFactory },
  mixins: [CanvasExport, ConfiguratorTextureCanvas],
  props: {
    width: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    backgroundColor: {
      type: String,
      default: undefined,
    },
    glassDiameter: {
      type: Number,
      default: undefined,
    },
    glassHeight: {
      type: Number,
      default: undefined,
    },
    textureSrc: {
      type: String,
      default: undefined,
    },
    printingAreaOffsetTop: {
      type: Number,
      default: 0,
    },
    layers: {
      type: Array,
      default: () => [],
      validator(v) {
        return v.every(layer => 'id' in layer);
      },
    },
    color: {
      type: String,
      default: undefined,
    },
  },
  data() {
    return {
      promiseHelper: new PromiseHelper(),
      textureImg: null,
      graduationImg: null,
      localLayers: [],
      renderQueue: [],
    };
  },
  computed: {
    ...mapMiscState(['debugPrintingArea']),
    ...mapConfiguratorGetters({
      activeGraduationImage: ConfiguratorGetters.GET_ACTIVE_GRADUATION_IMAGE,
      graduationImageWidth: ConfiguratorGetters.GET_ACTIVE_GRADUATION_WIDTH,
      graduationPositionOffset: ConfiguratorGetters.GET_GRADUATION_POSITION,
      hasGraduation: ConfiguratorGetters.IS_GRADUATION_VISIBLE,
    }),
    stageDimensions() {
      return {
        width: this.mmToPx(
          this.fullTexture ? getPerimeter(this.glassDiameter) : this.width
        ),
        height: this.mmToPx(
          this.fullTexture || this.hasGraduation
            ? this.glassHeight
            : this.height
        ),
      };
    },
    hasBackground() {
      return this.fullTexture || this.includeBackground;
    },
    stageConfig() {
      return {
        ...this.stageDimensions,
      };
    },
    backgroundRectConfig() {
      return {
        ...this.stageDimensions,
        x: 0,
        y: 0,
        fill: this.backgroundColor,
      };
    },
    printingAreaConfig() {
      const config = {
        y:
          this.fullTexture || this.hasGraduation
            ? this.mmToPx(this.printingAreaOffsetTop)
            : 0,
        width: this.mmToPx(this.width),
        height: this.mmToPx(this.height),
      };

      config.x = (this.stageDimensions.width - config.width) / 2;
      config.clip = {
        x: 0,
        y: 0,
        width: config.width,
        height: config.height,
      };

      return config;
    },
    groupNodeCommonConfig() {
      return {
        x: 0,
        y: 0,
        width: this.printingAreaConfig.width,
        height: this.printingAreaConfig.height,
      };
    },
    textureImgConfig() {
      return {
        ...this.groupNodeCommonConfig,
        image: this.textureImg,
      };
    },
    graduationImgConfig() {
      const rawXposition = this.mmToPx(
        (this.width - this.graduationImageWidth) *
          ((this.graduationPositionOffset || 0) / 100)
      );

      const roundedXposition =
        Math.round((rawXposition + Number.EPSILON) * 100) / 100;

      return {
        x: roundedXposition,
        y: 0,
        width: this.mmToPx(this.graduationImageWidth),
        height: this.mmToPx(this.glassHeight),
        image: this.graduationImg,
      };
    },
    printingAreaDebugConfig() {
      return {
        ...this.groupNodeCommonConfig,
        stroke: 'red',
      };
    },
    imageLayers() {
      return this.localLayers.filter(layer => layer.type === ZoneType.IMAGE);
    },
    textLayers() {
      return this.localLayers.filter(layer => layer.type === ZoneType.TEXT);
    },
  },
  watch: {
    fullTexture: {
      handler(v) {
        if (v) {
          this.validateGlassDiameter();
          this.validateGlassHeight();
          this.validateGlassBackgroundColor();
        }
      },
      immediate: true,
    },
    async renderQueue(queue) {
      if (queue.length && !this.isLayerProcessing()) {
        // Re-request LAYERS_READY before processing so any concurrent renderReady() call
        // waits for this new render cycle instead of resolving with the previous fulfilled promise.
        this.promiseHelper.request(PromiseKey.LAYERS_READY);

        await this.processRenderQueue();

        this.promiseHelper.resolve(PromiseKey.LAYERS_READY);
      }
    },
    async activeGraduationImage(graduationImgSrc) {
      if (graduationImgSrc) {
        this.graduationImg = await loadImageAndCache(graduationImgSrc);
      }
    },
  },
  created() {
    this.promiseHelper.request(PromiseKey.DOM_READY);
    this.promiseHelper.request(PromiseKey.LAYERS_READY);

    this.$watch(
      'textureSrc',
      async v => {
        this.promiseHelper.request(PromiseKey.TEXTURE_IMAGE);

        const graduationImgSrc = this.activeGraduationImage;
        if (graduationImgSrc) {
          this.graduationImg = await loadImageAndCache(graduationImgSrc);
        }

        if (v) {
          this.textureImg = await loadImageAndCache(v, true);

          checkImageSizeNotZero(this.textureImg);
        } else if (this.textureImg) {
          this.textureImg = null;
        }

        this.promiseHelper.resolve(PromiseKey.TEXTURE_IMAGE);
      },
      { immediate: true }
    );

    this.$watch(
      function () {
        // Call properties that need to trigger a new render when updated
        void this.resolution;
        void this.layers;
        void this.printingAreaConfig;

        return Date.now();
      },
      function () {
        const params = {
          resolution: cloneDeep(this.resolution),
          layers: cloneDeep(this.layers),
          printingAreaConfig: cloneDeep(this.printingAreaConfig),
        };

        this.renderQueue.push(params);
      },
      { immediate: true }
    );
  },
  mounted() {
    this.$nextTick(() => {
      this.promiseHelper.resolve(PromiseKey.DOM_READY);
    });
  },
  methods: {
    isLayerProcessing() {
      return (
        this.promiseHelper.has(PromiseKey.LAYERS_PROCESSING) &&
        this.promiseHelper.isPending(PromiseKey.LAYERS_PROCESSING)
      );
    },
    async processRenderQueue() {
      if (!this.isLayerProcessing()) {
        this.promiseHelper.request(PromiseKey.LAYERS_PROCESSING);
      }

      const params = this.renderQueue[this.renderQueue.length - 1];

      // Clear queue
      this.renderQueue = [];

      await this.updateLocalLayers(params);

      if (this.renderQueue.length) {
        await this.processRenderQueue();
      }

      this.promiseHelper.resolve(PromiseKey.LAYERS_PROCESSING);
    },
    async updateLocalLayers({ resolution, layers, printingAreaConfig }) {
      const mmToPxCb = v => (v !== undefined && this.mmToPx(v)) || v;
      const fontSizeTransform = v => scale(v, Resolution.WEB, resolution);

      const localLayers = [];

      // Using for-loop because of async/await
      for (let index = 0, len = layers.length; index < len; index++) {
        const layer = layers[index];

        if (layer._customization || layer.defaultValue) {
          /** @type {ZoneFullText|ZoneFullCustomImage|ZoneFullCustomText} */
          const outputLayer = transformProps(
            layer,
            ['x', 'y', 'width', 'height'],
            mmToPxCb
          );

          if (layer.fontSize) {
            outputLayer.fontSize = fontSizeTransform(layer.fontSize);
          }

          if (layer._customization) {
            // Text layers
            if (
              layer._customization.type === ZoneType.TEXT &&
              layer._customization.text
            ) {
              if (layer._customization.fontSize) {
                outputLayer._customization.fontSize = fontSizeTransform(
                  layer._customization.fontSize
                );
              }

              outputLayer._customization = transformProps(
                outputLayer._customization,
                ['x', 'y'],
                mmToPxCb
              );
            }

            // Image layers
            if (
              layer._customization.type === ZoneType.IMAGE &&
              layer._customization.url
            ) {
              const pxValues = {
                width: this.mmToPx(
                  layer._customization.width * layer._customization.scaleX
                ),
                height: this.mmToPx(
                  layer._customization.height * layer._customization.scaleY
                ),
                x: this.mmToPx(layer._customization.x),
                y: this.mmToPx(layer._customization.y),
              };
              const layerPxValues = {
                width: this.mmToPx(layer.width),
                height: this.mmToPx(layer.height),
              };

              // Get original image size
              const fullSizeImage = await loadImageAndCache(
                layer._customization.url
              );
              const { width, height } = fullSizeImage;

              const cropConfig = {
                x:
                  pxValues.x < 0
                    ? Math.round(
                        scale(Math.abs(pxValues.x), pxValues.width, width)
                      )
                    : 0,
                y:
                  pxValues.y < 0
                    ? Math.round(
                        scale(Math.abs(pxValues.y), pxValues.height, height)
                      )
                    : 0,
                width: undefined,
                height: undefined,
              };
              const outputSize = { width: undefined, height: undefined };

              // Width
              // Right overflow
              if (pxValues.x < 0) {
                const value = Math.min(
                  pxValues.width - Math.abs(pxValues.x),
                  this.mmToPx(layer.width),
                  printingAreaConfig.width
                );

                cropConfig.width = Math.round(
                  scale(value, pxValues.width, width)
                );
                outputSize.width = Math.round(value);
              }
              // Left overflow
              else if (pxValues.x + pxValues.width > layerPxValues.width) {
                const value = layerPxValues.width - pxValues.x;
                cropConfig.width = Math.round(
                  scale(value, pxValues.width, width)
                );
                outputSize.width = Math.round(value);
              }
              // Width fits in printing area
              else {
                cropConfig.width = width;
                outputSize.width = pxValues.width;
              }

              // Height
              // Top overflow
              if (pxValues.y < 0) {
                const value = Math.min(
                  pxValues.height - Math.abs(pxValues.y),
                  this.mmToPx(layer.height),
                  printingAreaConfig.height
                );

                cropConfig.height = Math.round(
                  scale(value, pxValues.height, height)
                );
                outputSize.height = Math.round(value);
              }
              // Bottom overflow
              else if (pxValues.y + pxValues.height > layerPxValues.height) {
                const value = layerPxValues.height - pxValues.y;
                cropConfig.height = Math.round(
                  scale(value, pxValues.height, height)
                );
                outputSize.height = Math.round(value);
              }
              // height fits in printing area
              else {
                cropConfig.height = height;
                outputSize.height = pxValues.height;
              }

              // Crop the image
              const croppedImage = cropImage(
                fullSizeImage,
                cropConfig,
                outputSize
              );

              // Use toDataURL instead of ObjectURL: on Safari iOS, canvas.toBlob() silently fails
              // for large canvases (>16MB limit), producing an invalid ObjectURL (WebKitBlobResource error 1).
              outputLayer._customization = {
                ...outputLayer._customization,
                url: croppedImage.toDataURL(),
                scaleX: 1,
                scaleY: 1,
                x: Math.max(0, Math.round(pxValues.x)),
                y: Math.max(0, Math.round(pxValues.y)),
                width: Math.round(outputSize.width),
                height: Math.round(outputSize.height),
              };
            }
          }

          localLayers.push(outputLayer);
        }
      }

      this.localLayers = localLayers;
    },
    async renderReady() {
      const promiseHelper = this.promiseHelper;

      await promiseHelper.get(PromiseKey.DOM_READY);

      // Flush pending prop-change watchers before sampling their promises so a
      // theme switch has re-requested TEXTURE_IMAGE (and queued a new render
      // cycle) instead of resolving with the previous theme's fulfilled
      // promise, which would export the wrong background.
      await this.$nextTick();

      await Promise.all([
        promiseHelper.get(PromiseKey.TEXTURE_IMAGE),
        promiseHelper
          .get(PromiseKey.LAYERS_PROCESSING)
          .then(() => promiseHelper.get(PromiseKey.LAYERS_READY)),
      ]);

      // Wait for the layer components to be (re)mounted from localLayers before
      // collecting their refs: a slow-to-create layer (e.g. a heavy uploaded
      // photo) would otherwise be absent from $refs.layers and silently skipped
      // by the `|| []` fallback, dropping the layer from the export.
      await this.$nextTick();

      // Waiting for layers to be fully loaded (font/img fully loaded)
      await Promise.all(
        (this.$refs.layers || []).map(layer => layer.layerReady())
      );

      // Force a synchronous redraw so the exported stage reflects the committed
      // state. layerReady() only schedules a draw via batchDraw() (RAF), which
      // can land after toDataURL() on slow devices, leaving fonts/images
      // unrasterized (e.g. a hatched/corrupted "&" glyph).
      const stage = this.$refs.stage && this.$refs.stage.getStage();
      if (stage) {
        stage.draw();
      }
    },
    validateGlassDiameter() {
      if (!this.glassDiameter) {
        throw new Error(`The required prop 'glassDiameter' is missing.`);
      }
    },
    validateGlassHeight() {
      if (!this.glassHeight) {
        throw new Error(`The required prop 'glassHeight' is missing.`);
      }
    },
    validateGlassBackgroundColor() {
      if (!this.backgroundColor) {
        throw new Error(`The required prop 'backgroundColor' is missing`);
      }
    },
    /**
     *
     * @param {number} value
     * @return {Number}
     */
    mmToPx(value) {
      return mmToPx(value, this.resolution);
    },
    async toBase64() {
      await this.renderReady();

      return this.$refs.stage.getStage().toDataURL();
    },
    /**
     * Export the stage directly to a Blob, bypassing the base64 round-trip of
     * toBase64()/CanvasExport.toBlob(). At print resolution that base64 string
     * is several MB and piles up memory on Safari iOS across cross-sell
     * exports. Used for the upload textures, which only need a Blob.
     * @return {Promise<Blob>}
     */
    async toBlob() {
      await this.renderReady();

      const canvas = this.$refs.stage.getStage().toCanvas();

      try {
        return await canvasToBlob(canvas);
      } finally {
        // Free the print-resolution backing store immediately. toCanvas()
        // allocates a fresh ~30MB canvas at PRINT resolution; on Safari iOS the
        // GC is lazy so without this it lingers and piles up across cross-sell
        // exports until the tab is killed.
        canvas.width = 0;
        canvas.height = 0;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-texture-canvas {
  &--headless {
    @include sr-only();
  }
}
</style>
